// =============================================================================
// 📁 File: src/pages/ddo/DDOQueue.tsx
// =============================================================================
// CTO Recovery Prompt — DDO Queue / Worklist UI (UPDATED)
//
// Invariants:
// - Reads ONLY from backend-safe queue endpoint: GET /ddo/submissions
// - Filters are URL-driven (dashboard tiles → query params → queue state)
// - Tenant + auth handled centrally by axiosInstance
// - Assignment-ledger truth: backend returns only submissions assigned to DDO
// - Date handling must NEVER crash the page (no Invalid time value)
// - "Open" must navigate to tenant-scoped submission detail route
// =============================================================================

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { differenceInCalendarDays, format, parseISO, isValid } from "date-fns";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import {
  DDOBulkAvailableActionsResponse,
  DDOBulkTransitionResponse,
  fetchDDOQueue,
  fetchDDOBulkAvailableActions,
  DDOQueueItem,
  DDOQueueResponse,
  DDOQueueParams,
  submitDDOBulkTransition,
} from "@/api/ddoApi";
import { formatWorkflowActionLabel } from "@/api/workflowApi";
import { formatWorkflowStatusLabel } from "@/utils/semanticLabels";

import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";

import { ChevronLeft, ChevronRight, Filter, RotateCcw, Search } from "lucide-react";

// -----------------------------------------------------------------------------
// ✅ Date helpers (must never throw)
// -----------------------------------------------------------------------------
function toYMD(d: Date | null | undefined): string | null {
  if (!d) return null;
  if (!isValid(d)) return null;
  return format(d, "yyyy-MM-dd");
}

function parseYMD(s: string | null): Date | null {
  if (!s) return null;
  try {
    const d = parseISO(s);
    return isValid(d) ? d : null;
  } catch {
    return null;
  }
}

function parseBool(s: string | null): boolean {
  if (!s) return false;
  return ["1", "true", "yes", "on"].includes(s.toLowerCase());
}

function parseIntSafe(s: string | null, fallback: number): number {
  if (!s) return fallback;
  const n = Number(s);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : fallback;
}

function setOrDelete(sp: URLSearchParams, key: string, value: string | null) {
  if (value == null || value.trim() === "") sp.delete(key);
  else sp.set(key, value);
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return "-";
  try {
    const parsed = parseISO(value);
    return isValid(parsed) ? format(parsed, "dd MMM yyyy, HH:mm") : value;
  } catch {
    return value;
  }
}

function getAssignedAgeDays(value: string | null | undefined): number | null {
  if (!value) return null;
  try {
    const parsed = parseISO(value);
    if (!isValid(parsed)) return null;
    return Math.max(0, differenceInCalendarDays(new Date(), parsed));
  } catch {
    return null;
  }
}

function getAgingTone(days: number | null): string {
  if (days == null) return "border-gray-200 bg-gray-50 text-gray-600";
  if (days >= 7) return "border-red-200 bg-red-50 text-red-700";
  if (days >= 3) return "border-amber-200 bg-amber-50 text-amber-700";
  return "border-emerald-200 bg-emerald-50 text-emerald-700";
}

function formatAge(days: number | null): string {
  if (days == null) return "No age";
  if (days === 0) return "Today";
  if (days === 1) return "1 day";
  return `${days} days`;
}

function formatProjectionLabel(value: string | null | undefined): string {
  return formatWorkflowStatusLabel(value);
}

function getActionabilityTone(value: string | null | undefined): string {
  if (value === "actionable") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (value === "waiting_external" || value === "waiting_internal") {
    return "border-amber-200 bg-amber-50 text-amber-700";
  }
  if (value === "locked" || value === "completed") {
    return "border-gray-200 bg-gray-50 text-gray-700";
  }
  return "border-red-200 bg-red-50 text-red-700";
}

function getReviewStateTone(value: string | null | undefined): string {
  if (value === "resubmitted" || value === "returned_backward") {
    return "border-blue-200 bg-blue-50 text-blue-700";
  }
  if (value === "clarification_requested") {
    return "border-amber-200 bg-amber-50 text-amber-700";
  }
  if (value === "rejected") return "border-red-200 bg-red-50 text-red-700";
  if (value === "completed") return "border-gray-200 bg-gray-50 text-gray-700";
  return "border-slate-200 bg-slate-50 text-slate-700";
}

function getActionabilityCognition(row: DDOQueueItem): string {
  if (row.actionability_state === "actionable") {
    return row.executable_action_count === 1
      ? "One backend-authorized action is available."
      : `${row.executable_action_count} backend-authorized actions are available.`;
  }
  if (row.actionability_state === "waiting_external") {
    return "Waiting outside DDO control.";
  }
  if (row.actionability_state === "waiting_internal") {
    return "Waiting on an internal prerequisite.";
  }
  if (row.actionability_state === "locked") {
    return "Locked from DDO action.";
  }
  if (row.actionability_state === "completed") {
    return "DDO work appears complete.";
  }
  if (row.blocking_reason) {
    return formatProjectionLabel(row.blocking_reason);
  }
  return "No executable DDO action is currently exposed.";
}

function getReviewStateCognition(row: DDOQueueItem): string {
  if (row.review_state === "new_submission") {
    return "Newly assigned for DDO review.";
  }
  if (row.review_state === "under_review") {
    return "DDO review is in progress.";
  }
  if (row.review_state === "clarification_requested") {
    return "Returned to applicant for correction.";
  }
  if (row.review_state === "resubmitted") {
    return "Applicant response is ready to re-check.";
  }
  if (row.review_state === "returned_backward") {
    return "Returned from a later workflow stage.";
  }
  if (row.review_state === "completed") {
    return "No further DDO review expected.";
  }
  if (row.latest_governance_event) {
    return formatProjectionLabel(row.latest_governance_event);
  }
  return "Review state unavailable.";
}

const DDOQueuePage: React.FC = () => {
  const navigate = useNavigate();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [data, setData] = useState<DDOQueueResponse | null>(null);
  const [items, setItems] = useState<DDOQueueItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>("");

  // Pagination
  const [page, setPage] = useState<number>(() =>
    parseIntSafe(searchParams.get("page"), 1)
  );
  const [pageSize] = useState<number>(10);

  // Filters
  const [statusFilter, setStatusFilter] = useState<string>(() =>
    searchParams.get("status") || ""
  );
  const [programFilter, setProgramFilter] = useState<string>(() =>
    searchParams.get("program_code") || ""
  );
  const [academicYearFilter, setAcademicYearFilter] = useState<string>(() =>
    searchParams.get("academic_year") || ""
  );
  const [applicantNameFilter, setApplicantNameFilter] = useState<string>(() =>
    searchParams.get("applicant_name") || ""
  );

  const [dateRange, setDateRange] = useState<{
    startDate: Date | null;
    endDate: Date | null;
  }>(() => ({
    startDate: parseYMD(searchParams.get("date_from")),
    endDate: parseYMD(searchParams.get("date_to")),
  }));

  const [includeLocked, setIncludeLocked] = useState<boolean>(() =>
    parseBool(searchParams.get("include_locked"))
  );
  const [includeDeleted, setIncludeDeleted] = useState<boolean>(() =>
    parseBool(searchParams.get("include_deleted"))
  );
  const [actionableOnly, setActionableOnly] = useState<boolean>(() =>
    parseBool(searchParams.get("actionable_only"))
  );
  const [staleOnly, setStaleOnly] = useState<boolean>(() =>
    parseBool(searchParams.get("stale_only"))
  );
  const [lockedOnly, setLockedOnly] = useState<boolean>(() =>
    parseBool(searchParams.get("locked_only"))
  );
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [bulkDiscovery, setBulkDiscovery] =
    useState<DDOBulkAvailableActionsResponse | null>(null);
  const [bulkResult, setBulkResult] =
    useState<DDOBulkTransitionResponse | null>(null);
  const [bulkActionCode, setBulkActionCode] = useState<string>("");
  const [bulkRemarks, setBulkRemarks] = useState<string>("");
  const [bulkRiskConfirmed, setBulkRiskConfirmed] = useState<boolean>(false);
  const [bulkLoading, setBulkLoading] = useState<boolean>(false);
  const [bulkError, setBulkError] = useState<string>("");

  // ---------------------------------------------------------------------------
  // ✅ Canonical tenant resolution
  // ---------------------------------------------------------------------------
  const reserved = new Set(["ddo", "pm", "pc", "finance", "management", "login", "403"]);
  const paramSlug =
    tenantSlug && tenantSlug.trim() && !reserved.has(tenantSlug.trim())
      ? tenantSlug.trim()
      : null;

  const storedSlugRaw = sessionStorage.getItem("tenant_slug");
  const storedSlug =
    storedSlugRaw && storedSlugRaw.trim() && !reserved.has(storedSlugRaw.trim())
      ? storedSlugRaw.trim()
      : null;

  const slug = paramSlug ?? storedSlug ?? "default";

  // ---------------------------------------------------------------------------
  // ✅ URL → State
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const urlStatus = searchParams.get("status") || "";
    const urlProgram = searchParams.get("program_code") || "";
    const urlAcademicYear = searchParams.get("academic_year") || "";
    const urlApplicantName = searchParams.get("applicant_name") || "";
    const urlDateFrom = searchParams.get("date_from");
    const urlDateTo = searchParams.get("date_to");

    const urlIncludeLocked = parseBool(searchParams.get("include_locked"));
    const urlIncludeDeleted = parseBool(searchParams.get("include_deleted"));
    const urlActionableOnly = parseBool(searchParams.get("actionable_only"));
    const urlStaleOnly = parseBool(searchParams.get("stale_only"));
    const urlLockedOnly = parseBool(searchParams.get("locked_only"));
    const urlPage = parseIntSafe(searchParams.get("page"), 1);

    setStatusFilter(urlStatus);
    setProgramFilter(urlProgram);
    setAcademicYearFilter(urlAcademicYear);
    setApplicantNameFilter(urlApplicantName);
    setIncludeLocked(urlIncludeLocked);
    setIncludeDeleted(urlIncludeDeleted);
    setActionableOnly(urlActionableOnly);
    setStaleOnly(urlStaleOnly);
    setLockedOnly(urlLockedOnly);
    setPage(urlPage);

    setDateRange({
      startDate: parseYMD(urlDateFrom),
      endDate: parseYMD(urlDateTo),
    });
  }, [searchParams]);

  // ---------------------------------------------------------------------------
  // ✅ Load Queue
  // ---------------------------------------------------------------------------
  const loadQueue = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const params: DDOQueueParams = {
        page,
        page_size: pageSize,
        status: statusFilter.trim() || null,
        program_code: programFilter.trim() || null,
        academic_year: academicYearFilter.trim()
          ? Number(academicYearFilter.trim())
          : null,
        applicant_name: applicantNameFilter.trim() || null,
        date_from: toYMD(dateRange.startDate),
        date_to: toYMD(dateRange.endDate),
        include_locked: includeLocked,
        include_deleted: includeDeleted,
        actionable_only: actionableOnly,
        stale_only: staleOnly,
        locked_only: lockedOnly,
      };

      const res = await fetchDDOQueue(params);
      setData(res);
      setItems(res.items);
      setSelectedIds((current) =>
        current.filter((id) => res.items.some((item) => item.id === id))
      );
    } catch (err) {
      console.error(err);
      setError("Failed to load DDO queue.");
    } finally {
      setLoading(false);
    }
  }, [
    page,
    pageSize,
    statusFilter,
    programFilter,
    academicYearFilter,
    applicantNameFilter,
    dateRange.startDate,
    dateRange.endDate,
    includeLocked,
    includeDeleted,
    actionableOnly,
    staleOnly,
    lockedOnly,
  ]);

  useEffect(() => {
    void loadQueue();
  }, [loadQueue]);

  const canGoPrev = page > 1;
  const canGoNext = data ? page * pageSize < data.total : false;
  const activeFilterCount = [
    statusFilter.trim(),
    programFilter.trim(),
    academicYearFilter.trim(),
    applicantNameFilter.trim(),
    toYMD(dateRange.startDate),
    toYMD(dateRange.endDate),
    includeLocked ? "include_locked" : "",
    includeDeleted ? "include_deleted" : "",
    actionableOnly ? "actionable_only" : "",
    staleOnly ? "stale_only" : "",
    lockedOnly ? "locked_only" : "",
  ].filter(Boolean).length;

  const pageAgingSummary = useMemo(() => {
    const ages = items
      .map((item) => getAssignedAgeDays(item.assigned_at))
      .filter((age): age is number => age != null);

    return {
      oldest: ages.length > 0 ? Math.max(...ages) : null,
      urgent: ages.filter((age) => age >= 7).length,
      reviewSoon: ages.filter((age) => age >= 3 && age < 7).length,
    };
  }, [items]);

  const selectedSet = new Set(selectedIds);
  const allPageSelected =
    items.length > 0 && items.every((item) => selectedSet.has(item.id));
  const selectedAction = bulkDiscovery?.common_actions.find(
    (action) => action.action_code === bulkActionCode
  );

  const goToSubmission = (submissionId: number) => {
    navigate(`/${slug}/ddo/submissions/${submissionId}`);
  };

  const updateQueueParams = (nextPage: number) => {
    const sp = new URLSearchParams(searchParams);

    setOrDelete(sp, "status", statusFilter.trim() || null);
    setOrDelete(sp, "program_code", programFilter.trim() || null);
    setOrDelete(sp, "academic_year", academicYearFilter.trim() || null);
    setOrDelete(sp, "applicant_name", applicantNameFilter.trim() || null);
    setOrDelete(sp, "date_from", toYMD(dateRange.startDate));
    setOrDelete(sp, "date_to", toYMD(dateRange.endDate));
    setOrDelete(sp, "include_locked", includeLocked ? "true" : null);
    setOrDelete(sp, "include_deleted", includeDeleted ? "true" : null);
    setOrDelete(sp, "actionable_only", actionableOnly ? "true" : null);
    setOrDelete(sp, "stale_only", staleOnly ? "true" : null);
    setOrDelete(sp, "locked_only", lockedOnly ? "true" : null);
    setOrDelete(sp, "page", nextPage > 1 ? String(nextPage) : null);

    setSearchParams(sp);
  };

  const applyFilters = () => {
    updateQueueParams(1);
  };

  const clearFilters = () => {
    setStatusFilter("");
    setProgramFilter("");
    setAcademicYearFilter("");
    setApplicantNameFilter("");
    setDateRange({ startDate: null, endDate: null });
    setIncludeLocked(false);
    setIncludeDeleted(false);
    setActionableOnly(false);
    setStaleOnly(false);
    setLockedOnly(false);
    setSearchParams(new URLSearchParams());
  };

  const goToPage = (nextPage: number) => {
    setPage(nextPage);
    updateQueueParams(nextPage);
  };

  const toggleSelection = (submissionId: number) => {
    setSelectedIds((current) =>
      current.includes(submissionId)
        ? current.filter((id) => id !== submissionId)
        : [...current, submissionId]
    );
    setBulkDiscovery(null);
    setBulkResult(null);
    setBulkActionCode("");
    setBulkRiskConfirmed(false);
    setBulkError("");
  };

  const togglePageSelection = () => {
    if (allPageSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(items.map((item) => item.id));
    }
    setBulkDiscovery(null);
    setBulkResult(null);
    setBulkActionCode("");
    setBulkRiskConfirmed(false);
    setBulkError("");
  };

  const discoverBulkActions = async () => {
    if (selectedIds.length === 0) return;

    try {
      setBulkLoading(true);
      setBulkError("");
      setBulkResult(null);
      setBulkActionCode("");
      setBulkRiskConfirmed(false);

      const discovery = await fetchDDOBulkAvailableActions(selectedIds);
      setBulkDiscovery(discovery);
    } catch (err: any) {
      console.error(err);
      setBulkDiscovery(null);
      setBulkError(err?.response?.data?.detail ?? "Failed to resolve bulk actions.");
    } finally {
      setBulkLoading(false);
    }
  };

  const executeBulkAction = async () => {
    if (!bulkActionCode || !bulkRiskConfirmed) return;

    try {
      setBulkLoading(true);
      setBulkError("");
      const result = await submitDDOBulkTransition({
        submission_ids: selectedIds,
        action_code: bulkActionCode,
        remarks: bulkRemarks.trim() || undefined,
      });
      setBulkResult(result);
      setBulkRiskConfirmed(false);
      await loadQueue();
    } catch (err: any) {
      console.error(err);
      setBulkError(err?.response?.data?.detail ?? "Bulk transition failed.");
    } finally {
      setBulkLoading(false);
    }
  };

  const renderRangeLabel = () => {
    if (!data) return null;
    if (data.total === 0) return "No submissions";

    const from = (page - 1) * pageSize + 1;
    const to = Math.min(page * pageSize, data.total);
    return `Showing ${from}–${to} of ${data.total}`;
  };

  const queueViewTitle = actionableOnly
    ? "Awaiting my action"
    : staleOnly
      ? "Stale Assignments"
      : lockedOnly
        ? "Locked Applications"
    : statusFilter.trim()
      ? `${formatProjectionLabel(statusFilter)} submissions`
      : includeLocked
        ? "My Total Applications"
        : "Visible applications";

  const queueViewHelp = actionableOnly
    ? "Only assigned submissions where the backend exposes at least one executable DDO action."
    : staleOnly
      ? "Actionable assignments older than 48 hours."
      : lockedOnly
        ? "Applications waiting outside DDO control or explicitly locked."
    : statusFilter.trim()
      ? `Filtered to backend status: ${statusFilter.trim()}.`
      : includeLocked
        ? "All non-deleted applications assigned to you, including locked rows."
        : "Non-deleted, unlocked applications assigned to you. Locked rows are hidden unless Include locked is enabled.";

  const activeFilterBadges = [
    statusFilter.trim() ? `Status: ${statusFilter.trim()}` : null,
    programFilter.trim() ? `Program: ${programFilter.trim()}` : null,
    academicYearFilter.trim() ? `Academic year: ${academicYearFilter.trim()}` : null,
    applicantNameFilter.trim() ? `Applicant: ${applicantNameFilter.trim()}` : null,
    toYMD(dateRange.startDate) ? `From: ${toYMD(dateRange.startDate)}` : null,
    toYMD(dateRange.endDate) ? `To: ${toYMD(dateRange.endDate)}` : null,
    includeLocked ? "Including locked" : null,
    includeDeleted ? "Including deleted" : null,
    actionableOnly ? "Awaiting my action only" : null,
    staleOnly ? "Stale assignments only" : null,
    lockedOnly ? "Locked applications only" : null,
  ].filter((badge): badge is string => Boolean(badge));

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold">DDO Worklist</h1>
          <div className="text-sm text-muted-foreground">
            Tenant: <span className="font-medium text-gray-900">{slug}</span>
          </div>
        </div>
        <Button variant="outline" onClick={() => void loadQueue()} disabled={loading}>
          Refresh
        </Button>
      </div>

      <div className="grid gap-3 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm text-muted-foreground">
              {queueViewTitle}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">{data?.total ?? 0}</div>
            <div className="text-xs text-muted-foreground">{queueViewHelp}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm text-muted-foreground">Oldest on Page</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-semibold">
              {formatAge(pageAgingSummary.oldest)}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-sm text-muted-foreground">Aging Risk</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className={getAgingTone(7)}>
                {pageAgingSummary.urgent} urgent
              </Badge>
              <Badge variant="outline" className={getAgingTone(3)}>
                {pageAgingSummary.reviewSoon} due soon
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Filter className="h-4 w-4" />
            Filters
            {activeFilterCount > 0 ? (
              <Badge variant="secondary">{activeFilterCount}</Badge>
            ) : null}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-4">
            <Input
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
              placeholder="Status"
            />
            <Input
              value={programFilter}
              onChange={(event) => setProgramFilter(event.target.value)}
              placeholder="Program code"
            />
            <Input
              value={academicYearFilter}
              onChange={(event) => setAcademicYearFilter(event.target.value)}
              placeholder="Academic year"
              inputMode="numeric"
            />
            <Input
              value={applicantNameFilter}
              onChange={(event) => setApplicantNameFilter(event.target.value)}
              placeholder="Applicant name"
            />
          </div>

          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div className="flex flex-wrap items-end gap-3">
              <label className="flex flex-col text-sm">
                <span className="font-medium text-muted-foreground">From</span>
                <input
                  type="date"
                  value={toYMD(dateRange.startDate) ?? ""}
                  onChange={(event) =>
                    setDateRange((current) => ({
                      ...current,
                      startDate: parseYMD(event.target.value),
                    }))
                  }
                  className="rounded border px-3 py-2"
                />
              </label>
              <label className="flex flex-col text-sm">
                <span className="font-medium text-muted-foreground">To</span>
                <input
                  type="date"
                  value={toYMD(dateRange.endDate) ?? ""}
                  onChange={(event) =>
                    setDateRange((current) => ({
                      ...current,
                      endDate: parseYMD(event.target.value),
                    }))
                  }
                  className="rounded border px-3 py-2"
                />
              </label>
              <Button onClick={applyFilters}>
                <Search className="h-4 w-4 mr-2" />
                Apply
              </Button>
              <Button variant="outline" onClick={clearFilters}>
                <RotateCcw className="h-4 w-4 mr-2" />
                Clear
              </Button>
            </div>
            <div className="flex flex-wrap gap-4 pb-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={includeLocked}
                  onChange={(event) => setIncludeLocked(event.target.checked)}
                />
                Include locked
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={includeDeleted}
                  onChange={(event) => setIncludeDeleted(event.target.checked)}
                />
                Include deleted
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={actionableOnly}
                  onChange={(event) => setActionableOnly(event.target.checked)}
                />
                Awaiting my action only
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Bulk Governance Actions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-muted-foreground">
              {selectedIds.length} selected from the current backend queue page.
            </div>
            <Button
              variant="secondary"
              disabled={selectedIds.length === 0 || bulkLoading}
              onClick={discoverBulkActions}
            >
              {bulkLoading ? "Checking..." : "Check Bulk Actions"}
            </Button>
          </div>

          {bulkError ? <p className="text-sm text-red-600">{bulkError}</p> : null}

          {bulkDiscovery ? (
            <div className="space-y-3 rounded-md border p-3">
              {bulkDiscovery.common_actions.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No common backend-authorized action is available for this selection.
                </p>
              ) : (
                <>
                  <div className="text-sm font-medium">
                    Backend-authorized common actions
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {bulkDiscovery.common_actions.map((action) => (
                      <Button
                        key={action.action_code}
                        variant={
                          bulkActionCode === action.action_code
                            ? "default"
                            : "outline"
                        }
                        onClick={() => {
                          setBulkActionCode(action.action_code);
                          setBulkRiskConfirmed(false);
                        }}
                      >
                        {formatWorkflowActionLabel(action.action_code)} → {formatWorkflowStatusLabel(action.to_status)}
                      </Button>
                    ))}
                  </div>
                </>
              )}

              {selectedAction ? (
                <div className="space-y-3 rounded-md border bg-amber-50 p-3">
                  <div className="text-sm font-medium">
                    Confirm bulk transition risk
                  </div>
                  <p className="text-sm text-muted-foreground">
                    This will attempt `{selectedAction.action_code}` on {selectedIds.length} selected submission(s).
                    The backend will execute per item and may return partial failures.
                  </p>
                  <Input
                    value={bulkRemarks}
                    onChange={(event) => setBulkRemarks(event.target.value)}
                    placeholder="Optional remarks applied to every selected item"
                  />
                  <label className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={bulkRiskConfirmed}
                      onChange={(event) => setBulkRiskConfirmed(event.target.checked)}
                    />
                    <span>
                      I understand this is a governance mutation and partial failures must be reviewed in each submission timeline.
                    </span>
                  </label>
                  <Button
                    variant="destructive"
                    disabled={!bulkRiskConfirmed || bulkLoading}
                    onClick={executeBulkAction}
                  >
                    Execute Bulk Action
                  </Button>
                </div>
              ) : null}

              {bulkDiscovery.submissions.some((item) => item.error) ? (
                <div className="space-y-1 text-sm">
                  <div className="font-medium">Discovery issues</div>
                  {bulkDiscovery.submissions
                    .filter((item) => item.error)
                    .map((item) => (
                      <div key={item.submission_id} className="text-muted-foreground">
                        #{item.submission_id}: {item.error}
                      </div>
                    ))}
                </div>
              ) : null}
            </div>
          ) : null}

          {bulkResult ? (
            <div className="rounded-md border p-3 text-sm">
              <div className="font-medium">
                Bulk result: {bulkResult.success} succeeded, {bulkResult.failure} failed
              </div>
              <div className="mt-2 space-y-1">
                {bulkResult.results.map((result) => (
                  <button
                    key={result.submission_id}
                    type="button"
                    onClick={() => goToSubmission(result.submission_id)}
                    className="block text-left text-muted-foreground hover:text-gray-900"
                  >
                    #{result.submission_id}: {result.status}
                    {result.to_status ? ` → ${formatWorkflowStatusLabel(result.to_status)}` : ""}
                    {result.error ? ` (${result.error})` : ""}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{queueViewTitle}</CardTitle>
        </CardHeader>

        <CardContent className="space-y-4">
          {activeFilterBadges.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {activeFilterBadges.map((badge) => (
                <Badge key={badge} variant="secondary">
                  {badge}
                </Badge>
              ))}
            </div>
          ) : null}

          {loading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, idx) => (
                <Skeleton key={idx} className="h-10 w-full" />
              ))}
            </div>
          ) : null}

          {!loading && items.length === 0 ? (
            <div className="text-sm text-muted-foreground">No results.</div>
          ) : null}

          {!loading && items.length > 0 ? (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <input
                        type="checkbox"
                        checked={allPageSelected}
                        onChange={togglePageSelection}
                        aria-label="Select all visible submissions"
                      />
                    </TableHead>
                    <TableHead>Submission</TableHead>
                    <TableHead>Student ID</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Review context</TableHead>
                    <TableHead>Actionability</TableHead>
                    <TableHead>Program</TableHead>
                    <TableHead>Academic year</TableHead>
                    <TableHead>Assigned at</TableHead>
                    <TableHead>Aging</TableHead>
                    <TableHead>Flags</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {items.map((row) => {
                    const id = row.id;
                    const ageDays = getAssignedAgeDays(row.assigned_at);
                    return (
                      <TableRow key={String(id)}>
                        <TableCell>
                          <input
                            type="checkbox"
                            checked={selectedSet.has(id)}
                            onChange={() => toggleSelection(id)}
                            aria-label={`Select submission ${id}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium">
                          {id ?? "-"}
                        </TableCell>
                        <TableCell>{row.student_id ?? "-"}</TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <Badge variant="secondary">
                              {row.status ?? "-"}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              Phase: {formatProjectionLabel(row.lifecycle_phase)}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <Badge
                              variant="outline"
                              className={getReviewStateTone(row.review_state)}
                            >
                              {formatProjectionLabel(row.review_state)}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {getReviewStateCognition(row)}
                              {row.latest_return_source
                                ? ` from ${formatProjectionLabel(row.latest_return_source)}`
                                : ""}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <Badge
                              variant="outline"
                              className={getActionabilityTone(row.actionability_state)}
                            >
                              {formatProjectionLabel(row.actionability_state)}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                              {getActionabilityCognition(row)}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>{row.program_code ?? "-"}</TableCell>
                        <TableCell>
                          {row.academic_year ?? "-"}
                        </TableCell>
                        <TableCell>
                          {formatDateTime(row.assigned_at)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={getAgingTone(ageDays)}>
                            {formatAge(ageDays)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {row.locked ? (
                              <Badge variant="outline" className="border-gray-300 bg-gray-50">
                                Locked
                              </Badge>
                            ) : null}
                            {row.assignment_state === "stale" ? (
                              <Badge variant="outline" className="border-amber-200 bg-amber-50 text-amber-700">
                                Stale assignment
                              </Badge>
                            ) : null}
                          </div>
                          {!row.locked && row.assignment_state !== "stale" ? (
                            <span className="text-muted-foreground">-</span>
                          ) : null}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!id}
                            onClick={() => id && goToSubmission(id)}
                          >
                            Open
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              <div className="flex items-center justify-between pt-2">
                <div className="text-xs text-muted-foreground">
                  {renderRangeLabel()}
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    disabled={!canGoPrev}
                    onClick={() => goToPage(Math.max(1, page - 1))}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Prev
                  </Button>
                  <Button
                    variant="outline"
                    disabled={!canGoNext}
                    onClick={() => goToPage(page + 1)}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            </>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
};

export default DDOQueuePage;
